import clsx from 'clsx';
import { ChevronDown, ChevronRight, Plus, Ban } from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ATTENDANCE_STATUS_LABELS, PUNCH_SOURCE_LABELS, type AttendanceDayDto, type AttendanceStatus, type AttendanceTotals, type PunchDto } from '@workforce/shared';
import { formatMinutes, t } from '@/lib/i18n';
import { DateTime } from 'luxon';
import { useTz } from '@/lib/timezone';
import { Badge, Button } from './ui';

type Tone = 'slate' | 'green' | 'amber' | 'red' | 'brand' | 'violet';
const STATUS_TONES: Record<AttendanceStatus, Tone> = {
  present: 'green',
  absent: 'red',
  leave: 'violet',
  holiday: 'brand',
  day_off: 'slate',
  incomplete: 'amber',
  in_progress: 'green',
  not_started: 'slate',
  not_employed: 'slate',
  not_tracked: 'slate',
  future: 'slate',
};

export function AttendanceBadge({ day }: { day: Pick<AttendanceDayDto, 'status' | 'holidayName' | 'missingIn' | 'missingOut'> }) {
  if (day.status === 'future') return null;
  let label = t(ATTENDANCE_STATUS_LABELS[day.status]);
  if (day.status === 'incomplete') label = day.missingIn ? t('Missing clock-in') : t('Missing clock-out');
  if (day.status === 'holiday' && day.holidayName) label = day.holidayName;
  return <Badge tone={STATUS_TONES[day.status]}>{label}</Badge>;
}

/** Signed minutes with colour: +overtime green, −shortfall red. */
export function Diff({ minutes, muted }: { minutes: number; muted?: boolean }) {
  if (muted) return <span className="text-slate-400">—</span>;
  if (minutes === 0) return <span className="text-slate-400">{formatMinutes(0)}</span>;
  return <span className={minutes > 0 ? 'text-emerald-700' : 'text-red-600'}>{minutes > 0 ? '+' : ''}{formatMinutes(minutes)}</span>;
}

export function TotalsBar({ totals }: { totals: AttendanceTotals }) {
  const items: { label: string; value: ReactNode }[] = [
    { label: t('Worked'), value: formatMinutes(totals.workedMinutes) },
    { label: t('Expected'), value: formatMinutes(totals.scheduledMinutes) },
    { label: t('Difference'), value: <Diff minutes={totals.differenceMinutes} /> },
    { label: t('Days present'), value: totals.presentDays },
    {
      label: t('Late'),
      value: totals.lateDays
        ? `${totals.lateDays > 1 ? t('{n} days', { n: totals.lateDays }) : t('{n} day', { n: totals.lateDays })} · ${formatMinutes(totals.lateMinutes)}`
        : '0',
    },
    { label: t('Left early'), value: totals.earlyLeaveDays },
    { label: t('Absent'), value: <span className={totals.absentDays ? 'text-red-600' : undefined}>{totals.absentDays}</span> },
    { label: t('Missing punch'), value: <span className={totals.missingPunchDays ? 'text-amber-700' : undefined}>{totals.missingPunchDays}</span> },
    { label: t('Leave days'), value: totals.leaveDays },
  ];
  return (
    <div className="grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-5 lg:grid-cols-9">
      {items.map((i) => (
        <div key={i.label} className="bg-surface px-3 py-2.5">
          <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{i.label}</p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{i.value}</p>
        </div>
      ))}
    </div>
  );
}

export function PunchList({
  punches,
  zone,
  onVoid,
}: {
  punches: PunchDto[];
  zone: string;
  onVoid?: (p: PunchDto) => void;
}) {
  const { fmt } = useTz();
  if (!punches.length) return <p className="text-sm text-slate-500">{t('No punches.')}</p>;
  return (
    <ul className="space-y-1.5">
      {punches.map((p) => (
        <li key={p.id} className={clsx('flex flex-wrap items-center gap-x-3 gap-y-1 text-sm', p.voidedAt && 'opacity-60')}>
          <span className={clsx('w-14 font-mono tabular-nums', p.voidedAt && 'line-through')}>{fmt(p.occurredAt, zone, 'HH:mm')}</span>
          <Badge tone={p.resolvedType === 'in' ? 'green' : 'slate'}>{p.resolvedType === 'in' ? t('In') : t('Out')}</Badge>
          <span className="text-xs text-slate-500">
            {p.source === 'admin' && p.createdBy ? t('Added by {name}', { name: p.createdBy }) : t(PUNCH_SOURCE_LABELS[p.source])}
            {p.deviceName ? ` · ${p.deviceName}` : ''}
            {p.note ? ` · “${p.note}”` : ''}
          </span>
          {p.voidedAt && (
            <span className="text-xs text-red-600">
              {p.voidedBy ? t('Voided by {name}', { name: p.voidedBy }) : t('Voided')}
              {p.voidReason ? `: ${p.voidReason}` : ''}
            </span>
          )}
          {onVoid && !p.voidedAt && (
            <button className="ms-auto inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline" onClick={() => onVoid(p)}>
              <Ban className="h-3 w-3" /> {t('Void')}
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Day-by-day attendance table. With `showEmployee` it lists one row per employee (day view);
 * otherwise one row per date. Rows with punches can expand to show them.
 */
export function AttendanceTable({
  days,
  showEmployee,
  onVoid,
  onAdd,
  employeeLink,
}: {
  days: AttendanceDayDto[];
  showEmployee?: boolean;
  onVoid?: (p: PunchDto, day: AttendanceDayDto) => void;
  onAdd?: (day: AttendanceDayDto) => void;
  employeeLink?: (d: AttendanceDayDto) => string;
}) {
  const { fmt } = useTz();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const expandable = days.some((d) => d.punches);
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-start text-xs font-medium uppercase tracking-wide text-slate-500">
            {expandable && <th className="w-8 px-2 py-2" />}
            <th className="px-3 py-2">{showEmployee ? t('Employee') : t('Date')}</th>
            <th className="px-3 py-2">{t('Status')}</th>
            <th className="px-3 py-2">{t('In')}</th>
            <th className="px-3 py-2">{t('Out')}</th>
            <th className="px-3 py-2 text-end">{t('Worked')}</th>
            <th className="hidden px-3 py-2 text-end sm:table-cell">{t('Expected')}</th>
            <th className="hidden px-3 py-2 text-end md:table-cell">{t('Late')}</th>
            <th className="hidden px-3 py-2 text-end md:table-cell">{t('Early')}</th>
            <th className="px-3 py-2 text-end">+/−</th>
            {onAdd && <th className="px-3 py-2" />}
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const key = `${d.employeeId}:${d.date}`;
            const isOpen = open[key];
            const noWork = ['future', 'not_employed', 'not_tracked', 'day_off', 'holiday', 'leave', 'not_started', 'in_progress'].includes(d.status) && !d.workedMinutes;
            return (
              <Fragment key={key}>
                <tr
                  className={clsx('border-b border-slate-100', expandable && 'cursor-pointer hover:bg-slate-50', d.status === 'future' && 'text-slate-400')}
                  onClick={() => expandable && setOpen((o) => ({ ...o, [key]: !o[key] }))}
                >
                  {expandable && (
                    <td className="px-2 py-2 text-slate-400">
                      {d.punches && d.punches.length > 0 ? isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" /> : null}
                    </td>
                  )}
                  <td className="whitespace-nowrap px-3 py-2">
                    {showEmployee ? (
                      employeeLink ? (
                        <Link to={employeeLink(d)} className="font-medium text-brand-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                          {d.employeeName}
                        </Link>
                      ) : (
                        <span className="font-medium">{d.employeeName}</span>
                      )
                    ) : (
                      <span className="font-medium">{DateTime.fromISO(d.date).toFormat('ccc dd LLL')}</span>
                    )}
                    {d.locked && <span className="ms-1.5 text-[10px] text-slate-400" title={t('Reporting period closed')}>🔒</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <AttendanceBadge day={d} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                    {d.firstIn ? fmt(d.firstIn, d.timezone, 'HH:mm') : d.missingIn ? <span className="text-amber-700">?</span> : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                    {d.lastOut ? fmt(d.lastOut, d.timezone, 'HH:mm') : d.missingOut && d.status !== 'in_progress' ? <span className="text-amber-700">?</span> : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-end font-medium tabular-nums">
                    {d.workedMinutes ? formatMinutes(d.workedMinutes) : '—'}
                    {d.hoursSource === 'computer' && (d.workedMinutes > 0 || d.firstIn) && (
                      <span className="ms-1 align-middle text-[10px] font-normal text-slate-400" title={t('Hours from computer activity (desktop agent), not clock-in/out')}>{t('computer')}</span>
                    )}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums text-slate-500 sm:table-cell">
                    {d.scheduledMinutes ? formatMinutes(d.scheduledMinutes) : '—'}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums md:table-cell">
                    {d.lateMinutes ? <span className="text-amber-700">{formatMinutes(d.lateMinutes)}</span> : '—'}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-2 text-end tabular-nums md:table-cell">
                    {d.earlyLeaveMinutes ? <span className="text-amber-700">{formatMinutes(d.earlyLeaveMinutes)}</span> : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-end tabular-nums">
                    <Diff minutes={d.differenceMinutes} muted={d.status === 'future' || (noWork && !d.scheduledMinutes)} />
                  </td>
                  {onAdd && (
                    <td className="px-3 py-2 text-end">
                      {!d.locked && d.status !== 'future' && d.status !== 'not_employed' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Plus className="h-3.5 w-3.5" />}
                          onClick={(e) => {
                            e.stopPropagation();
                            onAdd(d);
                          }}
                          aria-label={t('Add punch on {date}', { date: d.date })}
                        >
                          <span className="hidden lg:inline">{t('Punch')}</span>
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
                {isOpen && d.punches && (
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <td />
                    <td colSpan={onAdd ? 10 : 9} className="px-3 py-3">
                      <PunchList punches={d.punches} zone={d.timezone} onVoid={onVoid && !d.locked ? (p) => onVoid(p, d) : undefined} />
                      <p className="mt-2 text-[11px] text-slate-400">
                        {t('Times in {zone}', { zone: fmt(d.firstIn ?? d.scheduledStart ?? new Date(), d.timezone, 'ZZZZ') })} ·{' '}
                        {d.calcMode === 'first_last' ? t('first clock-in to last clock-out, minus break') : t('sum of in/out intervals')}
                      </p>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Quick date-range presets. */
export type RangePreset = 'period' | 'last_period' | 'week' | 'last_week' | 'month' | 'last_month' | 'custom';
type PeriodDates = { current: { startDate: string; endDate: string }; previous: { startDate: string; endDate: string } | null };
export function rangeFor(
  preset: RangePreset,
  zone: string,
  custom?: { from: string; to: string },
  periods?: PeriodDates,
): { from: string; to: string } {
  const now = DateTime.now().setZone(zone);
  switch (preset) {
    case 'period':
      if (periods) return { from: periods.current.startDate, to: periods.current.endDate };
      return rangeFor('month', zone);
    case 'last_period':
      if (periods?.previous) return { from: periods.previous.startDate, to: periods.previous.endDate };
      return rangeFor('last_month', zone);
    case 'week':
      return { from: now.startOf('week').toISODate()!, to: now.endOf('week').toISODate()! };
    case 'last_week': {
      const w = now.minus({ weeks: 1 });
      return { from: w.startOf('week').toISODate()!, to: w.endOf('week').toISODate()! };
    }
    case 'month':
      return { from: now.startOf('month').toISODate()!, to: now.endOf('month').toISODate()! };
    case 'last_month': {
      const m = now.minus({ months: 1 });
      return { from: m.startOf('month').toISODate()!, to: m.endOf('month').toISODate()! };
    }
    default:
      return custom ?? { from: now.toISODate()!, to: now.toISODate()! };
  }
}

export const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'period', label: 'This period' },
  { value: 'last_period', label: 'Last period' },
  { value: 'week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom…' },
];
